// M12.2 — Date range picker with preset pills + custom two-month calendar
// and a "Previous period" / "Year over year" comparison toggle. Emits
// bounds (startDate + endDate + compare) so the caller can forward
// straight to the /api/report contract.
//
// The picker defaults to "Previous period" compare and caps custom
// ranges at 365 days. We deliberately omit a "no compare" option — the
// v1 report always displayed a delta; M12.2 keeps that contract visible
// in the UI even though the backend still accepts compare=none for
// legacy share snapshots.

import { useMemo, useState } from "react";
import { DayPicker, type DateRange } from "react-day-picker";
import "react-day-picker/style.css";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { dayCount, formatDateRangeLabel } from "@shared/report/date-range";

// M12.12 — "custom" lets the user pick an arbitrary compare window via
// compareStart + compareEnd; "none" hides the delta chips entirely.
// The backend /api/report contract accepts all four values.
export type CompareMode = "previous" | "year_over_year" | "custom" | "none";

export interface DateRangeValue {
  startDate: string;
  endDate: string;
  compare: CompareMode;
  /** Required when compare === "custom"; the DateRangeSelector mints a
   *  sensible default (prior N days before startDate) the first time
   *  the user clicks "Custom" so the UI never emits an incomplete
   *  value. */
  compareStart?: string;
  compareEnd?: string;
}

export interface DateRangeSelectorProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  /** Optional override — otherwise `browser-local yesterday` anchors
   *  preset windows so pill detection matches what the server resolves. */
  today?: Date;
}

interface Preset {
  key: "last_7d" | "last_28d" | "last_90d";
  label: string;
  days: number;
}

const PRESETS: ReadonlyArray<Preset> = [
  { key: "last_7d", label: "7d", days: 7 },
  { key: "last_28d", label: "28d", days: 28 },
  { key: "last_90d", label: "90d", days: 90 },
];

const MAX_CUSTOM_DAYS = 365;

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseIso(s: string): Date {
  const [y, m, d] = s.split("-").map((p) => parseInt(p, 10));
  return new Date(y, m - 1, d);
}

function addDays(d: Date, days: number): Date {
  const next = new Date(d.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Compute preset bounds relative to "yesterday" in the browser's local
 * clock. The server resolves the same preset against the property's
 * timezone, so the two may disagree by a day around midnight UTC — we
 * prefer the browser-local view for pill detection because the user is
 * thinking in their own clock.
 */
function presetBounds(days: number, today: Date): { startDate: string; endDate: string } {
  const end = addDays(today, -1);
  const start = addDays(end, -(days - 1));
  return { startDate: isoDate(start), endDate: isoDate(end) };
}

function detectPreset(
  value: { startDate: string; endDate: string },
  today: Date,
): Preset["key"] | null {
  for (const preset of PRESETS) {
    const bounds = presetBounds(preset.days, today);
    if (bounds.startDate === value.startDate && bounds.endDate === value.endDate) {
      return preset.key;
    }
  }
  return null;
}

/**
 * Default compare-window bounds — the N days immediately before the
 * main window's startDate, matching the "Previous period" math. Used
 * as the seed when the user first flips the compare toggle to
 * "Custom", so the compare calendar opens on a sensible window
 * instead of empty.
 */
function defaultCompareBounds(
  startDate: string,
  endDate: string,
): {
  compareStart: string;
  compareEnd: string;
} {
  const start = parseIso(startDate);
  const end = parseIso(endDate);
  const days = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
  const prevEnd = addDays(start, -1);
  const prevStart = addDays(prevEnd, -(days - 1));
  return { compareStart: isoDate(prevStart), compareEnd: isoDate(prevEnd) };
}

/**
 * Human label for the "Comparing to" cell. Mirrors the math the server
 * uses in `resolvePeriods` so the UI shows the actual window the report
 * will be computed against — not just "a year ago" in the abstract.
 */
function computeCompareLabel(value: DateRangeValue): string | null {
  if (value.compare === "none") return null;
  if (value.compare === "custom") {
    if (value.compareStart && value.compareEnd) {
      return formatDateRangeLabel(value.compareStart, value.compareEnd);
    }
    return null;
  }
  if (value.compare === "year_over_year") {
    const start = parseIso(value.startDate);
    const end = parseIso(value.endDate);
    start.setFullYear(start.getFullYear() - 1);
    end.setFullYear(end.getFullYear() - 1);
    return formatDateRangeLabel(isoDate(start), isoDate(end));
  }
  // "previous" — same N days immediately prior.
  const { compareStart, compareEnd } = defaultCompareBounds(value.startDate, value.endDate);
  return formatDateRangeLabel(compareStart, compareEnd);
}

export function DateRangeSelector({ value, onChange, today }: DateRangeSelectorProps) {
  const anchorToday = useMemo(() => today ?? new Date(), [today]);
  const detected = useMemo(() => detectPreset(value, anchorToday), [value, anchorToday]);
  const [customOpen, setCustomOpen] = useState<boolean>(detected === null);
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(() =>
    detected === null
      ? { from: parseIso(value.startDate), to: parseIso(value.endDate) }
      : undefined,
  );
  const [rangeError, setRangeError] = useState<string | null>(null);

  // M12.12 — separate state for the custom-compare calendar so the user
  // can pick the compare window independently of the main window.
  const [compareCustomOpen, setCompareCustomOpen] = useState<boolean>(value.compare === "custom");
  const [pendingCompareRange, setPendingCompareRange] = useState<DateRange | undefined>(() =>
    value.compare === "custom" && value.compareStart && value.compareEnd
      ? { from: parseIso(value.compareStart), to: parseIso(value.compareEnd) }
      : undefined,
  );
  const [compareError, setCompareError] = useState<string | null>(null);

  const activePill: Preset["key"] | "custom" = detected ?? "custom";

  function selectPreset(preset: Preset) {
    const bounds = presetBounds(preset.days, anchorToday);
    setCustomOpen(false);
    setPendingRange(undefined);
    setRangeError(null);
    onChange({
      ...bounds,
      compare: value.compare,
      ...(value.compare === "custom" && value.compareStart && value.compareEnd
        ? { compareStart: value.compareStart, compareEnd: value.compareEnd }
        : {}),
    });
  }

  function openCustom() {
    setCustomOpen(true);
    setPendingRange({ from: parseIso(value.startDate), to: parseIso(value.endDate) });
    setRangeError(null);
  }

  function commitCustom(range: DateRange | undefined) {
    setPendingRange(range);
    if (!range?.from || !range?.to) return;
    const startDate = isoDate(range.from);
    const endDate = isoDate(range.to);
    if (dayCount(startDate, endDate) > MAX_CUSTOM_DAYS) {
      setRangeError(`Pick a range of ${MAX_CUSTOM_DAYS} days or fewer.`);
      return;
    }
    setRangeError(null);
    onChange({
      startDate,
      endDate,
      compare: value.compare,
      ...(value.compare === "custom" && value.compareStart && value.compareEnd
        ? { compareStart: value.compareStart, compareEnd: value.compareEnd }
        : {}),
    });
  }

  function setCompare(compare: CompareMode) {
    if (compare === "custom") {
      // Seed compareStart/compareEnd with the "previous period" default
      // so the user sees a populated calendar rather than an empty one.
      const seed =
        value.compareStart && value.compareEnd
          ? { compareStart: value.compareStart, compareEnd: value.compareEnd }
          : defaultCompareBounds(value.startDate, value.endDate);
      setPendingCompareRange({ from: parseIso(seed.compareStart), to: parseIso(seed.compareEnd) });
      setCompareCustomOpen(true);
      setCompareError(null);
      onChange({ ...value, compare, ...seed });
      return;
    }
    // Switching away from "custom" — drop the explicit bounds so the
    // server falls back to its computed previous/YoY window.
    setCompareCustomOpen(false);
    onChange({
      startDate: value.startDate,
      endDate: value.endDate,
      compare,
    });
  }

  function commitCompareCustom(range: DateRange | undefined) {
    setPendingCompareRange(range);
    if (!range?.from || !range?.to) return;
    const compareStart = isoDate(range.from);
    const compareEnd = isoDate(range.to);
    if (dayCount(compareStart, compareEnd) > MAX_CUSTOM_DAYS) {
      setCompareError(`Pick a range of ${MAX_CUSTOM_DAYS} days or fewer.`);
      return;
    }
    setCompareError(null);
    onChange({
      startDate: value.startDate,
      endDate: value.endDate,
      compare: "custom",
      compareStart,
      compareEnd,
    });
  }

  const rangeLabel = formatDateRangeLabel(value.startDate, value.endDate);
  // M12.12 — compute the label for the "Comparing to" cell. Null
  // means no compare window is active ("none"); rendered as an em-dash.
  const compareLabel = computeCompareLabel(value);

  const pillClass = (selected: boolean) =>
    cn(
      "inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
      selected
        ? "border border-purple-500 bg-purple-50 text-purple-700 ring-1 ring-purple-500"
        : "border border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300",
    );

  const anyPickerOpen = customOpen || (value.compare === "custom" && compareCustomOpen);

  return (
    // M12.12 — two-cell header (Date range | Comparing to) with a
    // full-height vertical divider, followed by an optional full-width
    // calendar row when the user opens a custom picker. The calendar
    // breaks out of the two-column grid intentionally — DayPicker at
    // numberOfMonths=2 is ~560px wide and looks cramped crammed into a
    // half-width column.
    <div className="overflow-hidden rounded-xl bg-white ring-1 ring-neutral-950/5">
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)]">
        <div className="space-y-3 p-5">
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
              Date range
            </p>
            <p className="text-sm text-neutral-700 tabular-nums">{rangeLabel}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((preset) => {
              const selected = activePill === preset.key;
              return (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => selectPreset(preset)}
                  aria-pressed={selected}
                  className={pillClass(selected)}
                >
                  {preset.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={openCustom}
              aria-pressed={activePill === "custom"}
              className={cn(pillClass(activePill === "custom"), "gap-1.5")}
            >
              <i className="fa-regular fa-calendar text-xs" aria-hidden="true" />
              Custom
            </button>
          </div>
        </div>
        <div aria-hidden="true" className="hidden bg-neutral-200 sm:block" />
        <div className="space-y-3 border-t border-neutral-200 p-5 sm:border-t-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
              Comparing to
            </p>
            <p className="text-sm text-neutral-700 tabular-nums">{compareLabel ?? "—"}</p>
          </div>
          <fieldset className="flex flex-wrap items-center gap-2">
            <legend className="sr-only">Comparison period</legend>
            {(
              [
                { key: "previous", label: "Previous period" },
                { key: "year_over_year", label: "Year over year" },
                { key: "custom", label: "Custom" },
                { key: "none", label: "None" },
              ] as const
            ).map((opt) => {
              const selected = value.compare === opt.key;
              return (
                <label key={opt.key} className={cn(pillClass(selected), "cursor-pointer")}>
                  <input
                    type="radio"
                    name="report-compare"
                    value={opt.key}
                    checked={selected}
                    onChange={() => setCompare(opt.key)}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              );
            })}
          </fieldset>
        </div>
      </div>
      {anyPickerOpen ? (
        <div className="space-y-4 border-t border-neutral-200 bg-neutral-50/40 p-5">
          {customOpen ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-medium text-neutral-700">
                  <span className="font-semibold text-purple-700">Date range:</span> pick a start
                  and end day — up to {MAX_CUSTOM_DAYS} days.
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCustomOpen(false);
                    setPendingRange(undefined);
                    setRangeError(null);
                  }}
                >
                  Close
                </Button>
              </div>
              <DayPicker
                mode="range"
                numberOfMonths={2}
                selected={pendingRange}
                onSelect={commitCustom}
                defaultMonth={pendingRange?.from ?? parseIso(value.startDate)}
                disabled={{ after: addDays(anchorToday, -1) }}
                style={
                  {
                    "--rdp-accent-color": "var(--color-purple-600, #7c3aed)",
                    "--rdp-accent-background-color": "var(--color-purple-50, #f5f3ff)",
                  } as React.CSSProperties
                }
              />
              {rangeError ? (
                <p role="alert" className="text-sm text-red-700">
                  {rangeError}
                </p>
              ) : null}
            </div>
          ) : null}
          {value.compare === "custom" && compareCustomOpen ? (
            <div className={cn("space-y-3", customOpen && "border-t border-neutral-200 pt-4")}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-medium text-neutral-700">
                  <span className="font-semibold text-purple-700">Comparing to:</span> pick a
                  reference window — eg. launch week or last quarter.
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCompareCustomOpen(false);
                  }}
                >
                  Close
                </Button>
              </div>
              <DayPicker
                mode="range"
                numberOfMonths={2}
                selected={pendingCompareRange}
                onSelect={commitCompareCustom}
                defaultMonth={
                  pendingCompareRange?.from ??
                  (value.compareStart ? parseIso(value.compareStart) : parseIso(value.startDate))
                }
                disabled={{ after: addDays(anchorToday, -1) }}
                style={
                  {
                    "--rdp-accent-color": "var(--color-purple-600, #7c3aed)",
                    "--rdp-accent-background-color": "var(--color-purple-50, #f5f3ff)",
                  } as React.CSSProperties
                }
              />
              {compareError ? (
                <p role="alert" className="text-sm text-red-700">
                  {compareError}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
